import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ad } from "@/types/ad";
import { useToast } from "@/hooks/use-toast";
import { buyNumberForAdEdge } from "@/integrations/supabase/twilioApi";

interface Props {
  ad: Ad | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onPurchased?: () => void;
}

export function BuyAdNumberDialog({ ad, open, onOpenChange, onPurchased }: Props) {
  const [country, setCountry] = useState('US');
  const [areaCode, setAreaCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const onSubmit = async () => {
    if (!ad) return;
    try {
      setLoading(true);
      const res = await buyNumberForAdEdge(ad.id, { country, areaCode: areaCode || undefined });
      toast({ title: 'Number purchased', description: res.e164 || 'Success' });
      onOpenChange(false);
      onPurchased?.();
    } catch (e: any) {
      let errorMessage = e.message;
      let errorTitle = 'Failed to buy number';
      
      // Handle specific error cases
      if (e.message.includes('AD_LIMIT_REACHED')) {
        errorTitle = 'Ad Limit Reached';
        errorMessage = 'You have reached the maximum number of ads (5) for this campaign. Please delete an existing ad or create a new campaign.';
      } else if (e.message.includes('CAMPAIGN_LIMIT_REACHED')) {
        errorTitle = 'Campaign Limit Reached';
        errorMessage = 'You have reached the maximum number of campaigns (5). Please delete an existing campaign or upgrade your plan.';
      }
      
      toast({ title: errorTitle, description: errorMessage, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Buy a tracking number</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Country (e.g., US)" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} />
          <Input placeholder="Area code (optional)" value={areaCode} onChange={(e) => setAreaCode(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={loading || !ad}>Buy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
